import { Input } from 'baseui/input'
import { lightTheme } from '@/customTheme'

interface TextInputFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoFocus?: boolean
  onEnterKey?: () => void
  /**
   * Forces the field's text direction. Use `ltr` for Latin codes, URLs or
   * numbers sitting inside an RTL page, where inheriting `rtl` would right-align
   * the value and put the caret on the wrong side.
   *
   * Note `auto` is not offered: for inputs it resolves from the *value* only, so
   * an empty field falls back to `ltr` and drags a translated placeholder to the
   * wrong edge. Leave it unset while empty instead.
   */
  dir?: 'ltr' | 'rtl'
  /** Set on the inner `input`, so a `<label for>` or a `focus()` can reach it. */
  id?: string
}

function TextInputField({
  value,
  onChange,
  placeholder,
  autoFocus,
  onEnterKey,
  dir,
  id,
}: TextInputFieldProps) {
  return (
    <Input
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange((e.target as HTMLInputElement).value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onEnterKey?.()
      }}
      overrides={{
        Root: {
          style: () => ({
            height: '48px',
            borderTopWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            borderRightWidth: '1px',
            borderTopStyle: 'solid',
            borderBottomStyle: 'solid',
            borderLeftStyle: 'solid',
            borderRightStyle: 'solid',
            borderTopColor: lightTheme.colors.grayScale100,
            borderBottomColor: lightTheme.colors.grayScale100,
            borderLeftColor: lightTheme.colors.grayScale100,
            borderRightColor: lightTheme.colors.grayScale100,
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            backgroundColor: lightTheme.colors.white,
            ':focus-within': {
              borderTopColor: lightTheme.colors.blackGray,
              borderBottomColor: lightTheme.colors.blackGray,
              borderLeftColor: lightTheme.colors.blackGray,
              borderRightColor: lightTheme.colors.blackGray,
            },
          }),
        },
        Input: {
          props: { ...(dir ? { dir } : {}), ...(id ? { id } : {}) },
          style: () => ({
            backgroundColor: lightTheme.colors.white,
            fontSize: '14px',
            color: lightTheme.colors.blackGray,
            ...(dir ? { direction: dir, textAlign: 'start' as const } : {}),
            '::placeholder': {
              color: lightTheme.colors.grayScale400,
            },
          }),
        },
      }}
    />
  )
}

export default TextInputField
